<template>
  <form
    ref="target"
    @click="activateSearch"
    @submit.prevent
    class="relative flex items-center h-8 p-1 transition-all duration-1000 border rounded-lg cursor-pointer hover:border-blue-300"
    :class="isActive ? 'w-72 border-blue-400 bg-white' : 'w-8 border-gray-300 hover:bg-blue-50'">
    <input
      v-model="searchQuery"
      @input="onInput"
      ref="searchInput"
      type="search"
      :placeholder="$t('common.actions.search')"
      class="w-full text-sm font-light text-gray-600 border-none rounded-full outline-none pl-7"
      :class="isActive ? 'block opacity-100' : 'hidden opacity-0'" />
    <span
      class="box-border absolute items-center w-full h-full transition-all duration-1000 top-2 left-1.5"
      :class="isActive ? 'text-blue-500 ' : 'text-gray-500 hover:text-blue-500 '">
      <IconSearch class="size-4" />
    </span>
    <span
      v-if="searchQuery"
      @click.stop="clearSearch"
      class="absolute text-gray-400 cursor-pointer right-2">
      <IconXmark class="size-4" />
    </span>
  </form>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue';
import { onClickOutside } from '@vueuse/core';
import IconSearch from '@/assets/icons/IconSearch.vue';
import IconXmark from '@/assets/icons/IconXmark.vue';

const searchInput = ref<HTMLElement | null>(null);
const target = ref<HTMLElement | null>(null);

onClickOutside(target, () => {
  if (searchQuery.value === '') isActive.value = false;
});

const searchQuery = defineModel<string>();
const emit = defineEmits(['updateSearch']);

const isActive = ref(false);
let timeout: ReturnType<typeof setTimeout> | undefined = undefined;

const activateSearch = () => {
  isActive.value = true;
  nextTick(() => {
    searchInput.value?.focus();
  });
};

const emitSearch = () => {
  emit('updateSearch');
};

const onInput = () => {
  clearTimeout(timeout);
  timeout = setTimeout(() => {
    emitSearch();
  }, 500);
};

const clearSearch = () => {
  if (!searchQuery.value) return;
  searchQuery.value = '';
  emitSearch();
};
</script>

<style scoped>
/* Remove browser default x icon */
input[type='search']::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
}

input[type='search']::-ms-clear {
  display: none;
  width: 0;
  height: 0;
}

input[type='search']::-ms-reveal {
  display: none;
  width: 0;
  height: 0;
}

input[type='search'] {
  -webkit-appearance: textfield;
  appearance: none;
}

.size-6 {
  width: 1.5rem;
  height: 1.5rem;
}
</style>
